import type { z } from 'zod';
import {
  Activity,
  Building,
  Building2,
  Earth,
  Globe,
  Hash,
  Mail,
  MapPin,
  Phone,
  Users,
} from 'lucide-react';

import type { ReportSchema } from '@ballerine/common';

import { ctw } from '@/common';
import { DataRow, EntityStatusIndicator } from '../../primitives';
import {
  filterValidStrings,
  resolveMerchantAddressDisplay,
  resolveMerchantEmailDisplay,
  resolveMerchantPhoneDisplay,
} from './utils';

type MerchantDetailsContentProps = {
  merchantDetails: NonNullable<z.infer<typeof ReportSchema>['data']>['merchantDetails'] | null;
  className?: string;
  showWebsite?: boolean;
  showAssociatedPersons?: boolean;
  showEntityStatus?: boolean;
  /**
   * Resolve `address` through the `physicalAddresses` / `physicalAddress` fallbacks instead of
   * reading `address` directly. Opt-in so callers that only ever populate `address` keep the
   * simpler read; DF relies on the fallbacks because its extractor can emit the array form.
   */
  resolveAddressFallbacks?: boolean;
  emptyLabel?: string;
};

export const MerchantDetailsContent: React.FC<MerchantDetailsContentProps> = ({
  merchantDetails,
  className,
  showWebsite = false,
  showAssociatedPersons = false,
  showEntityStatus = false,
  resolveAddressFallbacks = false,
  emptyLabel = 'Not Detected',
}) => {
  if (!merchantDetails) {
    return null;
  }

  const phoneDisplay = resolveMerchantPhoneDisplay(
    merchantDetails as typeof merchantDetails & {
      phoneNumbers?: string[] | null;
    },
  );
  const emailDisplay = resolveMerchantEmailDisplay(
    merchantDetails as typeof merchantDetails & {
      emailAddresses?: string[] | null;
    },
  );
  const addressDisplay = resolveAddressFallbacks
    ? resolveMerchantAddressDisplay(
        merchantDetails as typeof merchantDetails & {
          physicalAddresses?: string[] | null;
        },
      )
    : { text: merchantDetails.address ?? null, copy: merchantDetails.address || '' };

  return (
    <div className={ctw(className)}>
      <DataRow
        label="Company Name"
        dataTestId="merchant-detail"
        Icon={Building}
        value={merchantDetails.companyName}
        copyable={!!merchantDetails.companyName}
        copyValue={merchantDetails.companyName || ''}
        emptyLabel={emptyLabel}
        disableTranslation
      />

      <DataRow
        label="Alternative Company Names"
        dataTestId="merchant-detail"
        Icon={Building2}
        value={(() => {
          const names = filterValidStrings(merchantDetails.alternativeCompanyNames);

          if (!names.length) return null;

          return <span>{names.join(', ')}</span>;
        })()}
        copyable={!!filterValidStrings(merchantDetails.alternativeCompanyNames).length}
        copyValue={filterValidStrings(merchantDetails.alternativeCompanyNames).join(', ') || ''}
        emptyLabel={emptyLabel}
        disableTranslation
      />

      {showEntityStatus && (
        <DataRow
          label="Entity Status"
          dataTestId="merchant-detail"
          Icon={Activity}
          value={
            <EntityStatusIndicator
              status={(merchantDetails as Record<string, unknown>).status as string | null}
              emptyLabel={emptyLabel}
            />
          }
          copyable={!!(merchantDetails as Record<string, unknown>).status}
          copyValue={((merchantDetails as Record<string, unknown>).status as string) || ''}
          emptyLabel={emptyLabel}
        />
      )}

      <DataRow
        label="Registration Number"
        dataTestId="merchant-detail"
        Icon={Hash}
        value={merchantDetails.registrationNumber}
        copyable={!!merchantDetails.registrationNumber}
        copyValue={merchantDetails.registrationNumber || ''}
        emptyLabel={emptyLabel}
      />

      <DataRow
        label="Phone Number"
        dataTestId="merchant-detail"
        Icon={Phone}
        value={phoneDisplay.text}
        copyable={Boolean(phoneDisplay.text)}
        copyValue={phoneDisplay.copy}
        emptyLabel={emptyLabel}
      />

      <DataRow
        label="Email"
        dataTestId="merchant-detail"
        Icon={Mail}
        value={emailDisplay.text}
        copyable={Boolean(emailDisplay.text)}
        copyValue={emailDisplay.copy}
        emptyLabel={emptyLabel}
        disableTranslation
      />

      <DataRow
        label="Country"
        dataTestId="merchant-detail"
        Icon={Earth}
        value={merchantDetails.country}
        copyable={!!merchantDetails.country}
        copyValue={merchantDetails.country || ''}
        emptyLabel={emptyLabel}
      />

      <DataRow
        label="Address"
        dataTestId="merchant-detail"
        Icon={MapPin}
        value={addressDisplay.text}
        copyable={Boolean(addressDisplay.text)}
        copyValue={addressDisplay.copy}
        emptyLabel={emptyLabel}
      />

      {showWebsite && (
        <DataRow
          label="Website"
          dataTestId="merchant-detail"
          Icon={Globe}
          value={
            merchantDetails.website ? (
              <a
                href={merchantDetails.website}
                target="_blank"
                rel="noopener noreferrer"
                className="notranslate text-blue-600 hover:text-blue-800 hover:underline"
                translate="no"
              >
                {merchantDetails.website}
              </a>
            ) : null
          }
          copyable={!!merchantDetails.website}
          copyValue={merchantDetails.website || ''}
          emptyLabel={emptyLabel}
          disableTranslation
        />
      )}

      {showAssociatedPersons && (
        <DataRow
          label="Associated Persons"
          dataTestId="merchant-detail"
          Icon={Users}
          value={(() => {
            const persons = filterValidStrings(merchantDetails.associatedPersons);

            if (!persons.length) return undefined;

            return (
              <div className="space-y-1">
                {persons.map((person, index) => (
                  <div key={index} className="notranslate text-sm text-gray-900" translate="no">
                    {person}
                  </div>
                ))}
              </div>
            );
          })()}
          copyable={!!filterValidStrings(merchantDetails.associatedPersons).length}
          copyValue={filterValidStrings(merchantDetails.associatedPersons).join(', ') || ''}
          emptyLabel={emptyLabel}
          disableTranslation
        />
      )}
    </div>
  );
};
